// src/components/three/HeroEmblem.tsx
// ホームのヒーロー3Dの公開ラッパ。
// - three は React.lazy で動的import（メインバンドル非同梱）
// - 表示領域に入った時のみ Canvas をマウント
// - タブ非表示で描画停止
// - prefers-reduced-motion / WebGL非対応 / 低性能端末 は静止フォールバック
import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';

const GoldEmblemScene = lazy(() => import('./GoldEmblemScene'));

function prefersReducedMotion(): boolean {
    return (
        typeof window !== 'undefined' &&
        !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    );
}

function webglAvailable(): boolean {
    try {
        const canvas = document.createElement('canvas');
        return !!(
            window.WebGLRenderingContext &&
            (canvas.getContext('webgl') || canvas.getContext('experimental-webgl'))
        );
    } catch {
        return false;
    }
}

function lowPowerDevice(): boolean {
    // 論理コア数が少ない端末は静止表示にして安全側に倒す
    const cores = (navigator as any)?.hardwareConcurrency;
    return typeof cores === 'number' && cores > 0 && cores <= 4;
}

/** 静止フォールバック（CSSの金グラデ・エンブレム）。3Dが使えない全ケースで表示。 */
const StaticEmblem: React.FC<{ height: number | string }> = ({ height }) => (
    <Box
        aria-hidden
        sx={{
            width: '100%',
            height,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        }}
    >
        <Box
            sx={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                background:
                    'radial-gradient(circle at 35% 30%, #F0CE7E, #C9A227 55%, #7a6015 100%)',
                boxShadow:
                    '0 0 0 1px rgba(228,185,91,0.35), 0 10px 40px rgba(228,185,91,0.25)',
                clipPath:
                    'polygon(50% 0%, 90% 20%, 100% 60%, 75% 100%, 25% 100%, 0% 60%, 10% 20%)',
            }}
        />
    </Box>
);

interface Props {
    height?: number | string;
}

const HeroEmblem: React.FC<Props> = ({ height = 260 }) => {
    const wrapRef = useRef<HTMLDivElement>(null);
    const [inView, setInView] = useState(false);
    const [tabActive, setTabActive] = useState(true);
    const [use3D, setUse3D] = useState(false);

    // 3Dを使ってよい端末か初回に判定
    useEffect(() => {
        setUse3D(!prefersReducedMotion() && webglAvailable() && !lowPowerDevice());
    }, []);

    // 表示領域に入ったらマウント
    useEffect(() => {
        const el = wrapRef.current;
        if (!el || typeof IntersectionObserver === 'undefined') {
            setInView(true);
            return;
        }
        const io = new IntersectionObserver(
            (entries) => entries.forEach((e) => e.isIntersecting && setInView(true)),
            { rootMargin: '80px' }
        );
        io.observe(el);
        return () => io.disconnect();
    }, []);

    // タブ非表示で描画停止
    useEffect(() => {
        const onVis = () => setTabActive(!document.hidden);
        document.addEventListener('visibilitychange', onVis);
        return () => document.removeEventListener('visibilitychange', onVis);
    }, []);

    return (
        <div ref={wrapRef} style={{ width: '100%' }}>
            {use3D && inView ? (
                <Suspense fallback={<StaticEmblem height={height} />}>
                    <GoldEmblemScene active={tabActive} height={height} />
                </Suspense>
            ) : (
                <StaticEmblem height={height} />
            )}
        </div>
    );
};

export default HeroEmblem;
